/**
 * Shared helper that turns Step 8 inputs into a list of human-readable
 * incentive levers. Drives both the React summary box on the analysis
 * page and the equivalent panel at the top of the PDF report.
 *
 * Design notes:
 *  - Only "active" levers (non-zero amount or duration) appear. Zero-value
 *    items would clutter the panel and aren't part of the offered package.
 *  - Each lever has three pieces: a category label, a human description
 *    of the lever's terms, and an optional pre-discount nominal estimate
 *    of cost so the panel can show component contribution at-a-glance.
 *  - Pure — no formatting of currency/percent here. Callers choose their
 *    own formatter (web uses Intl.NumberFormat, PDF uses report/format).
 */

export interface IncentivePackageInputs {
  readonly step5?: {
    readonly directAnnualPayroll?: number;
  } | null;
  readonly step8?: {
    readonly jobGrantPctOfPayroll?: number;
    readonly jobGrantYears?: number;
    readonly infrastructureGrantTotal?: number;
    readonly infrastructureGrantYears?: number;
    readonly propertyTaxAbatementPct?: number;
    readonly propertyTaxAbatementYears?: number;
    /** Stepped/declining abatement schedule; overrides the flat pct/years pair above when present. */
    readonly propertyTaxAbatementSchedule?: readonly number[];
  } | null;
}

export interface IncentiveLever {
  /** Stable ID for testing and React keys. */
  readonly id: 'jobGrant' | 'infrastructure' | 'propertyTaxAbatement';
  /** Short category label, e.g. "Job Grant". */
  readonly label: string;
  /** Human-readable terms, e.g. "5% of payroll for 7 years". */
  readonly terms: string;
  /**
   * Nominal (undiscounted) estimated cost of this lever, when calculable
   * from inputs alone. Property tax abatement requires the actual property
   * tax bill, which is a calc-engine result — so it's null here and
   * callers should look at `summary.totalIncentive` for the real number.
   */
  readonly nominalCost: number | null;
}

export interface IncentivePackageSummary {
  readonly levers: readonly IncentiveLever[];
  /** True when no lever is active — caller can skip rendering the box. */
  readonly isEmpty: boolean;
}

/**
 * Build a structured summary of the offered incentive package from raw
 * AnalysisInput-shaped data. Does not look at calc-engine results; that
 * stays the caller's job (totalIncentive, perJobCost, etc.).
 */
export function summarizeIncentivePackage(inputs: IncentivePackageInputs): IncentivePackageSummary {
  const step5 = inputs.step5 ?? {};
  const step8 = inputs.step8 ?? {};

  const directAnnualPayroll = step5?.directAnnualPayroll ?? 0;
  const jobGrantPct = step8?.jobGrantPctOfPayroll ?? 0;
  const jobGrantYears = step8?.jobGrantYears ?? 0;
  const infraTotal = step8?.infrastructureGrantTotal ?? 0;
  const infraYears = step8?.infrastructureGrantYears ?? 0;
  const abatePct = step8?.propertyTaxAbatementPct ?? 0;
  const abateYears = step8?.propertyTaxAbatementYears ?? 0;
  const abateSchedule = step8?.propertyTaxAbatementSchedule ?? undefined;

  const levers: IncentiveLever[] = [];

  // Job grant — paid as a fraction of stabilized payroll over N years.
  if (jobGrantPct > 0 && jobGrantYears > 0) {
    levers.push({
      id: 'jobGrant',
      label: 'Job grant',
      terms: `${formatPct(jobGrantPct)} of direct payroll for ${formatYears(jobGrantYears)}`,
      nominalCost:
        directAnnualPayroll > 0 ? jobGrantPct * directAnnualPayroll * jobGrantYears : null,
    });
  }

  // Infrastructure grant — fixed dollar amount paid out over N years.
  if (infraTotal > 0) {
    const yearsClause = infraYears > 0 ? ` over ${formatYears(infraYears)}` : '';
    levers.push({
      id: 'infrastructure',
      label: 'Infrastructure grant',
      terms: `${formatUsd(infraTotal)}${yearsClause}`,
      nominalCost: infraTotal,
    });
  }

  // Property tax abatement — either a stepped schedule or N years at X% off.
  if (abateSchedule && abateSchedule.length > 0) {
    levers.push({
      id: 'propertyTaxAbatement',
      label: 'Property tax abatement',
      terms: `${formatSchedule(abateSchedule)} abated over ${formatYears(abateSchedule.length)}`,
      nominalCost: null, // depends on calc-engine real-property tax stream
    });
  } else if (abatePct > 0 && abateYears > 0) {
    levers.push({
      id: 'propertyTaxAbatement',
      label: 'Property tax abatement',
      terms: `${formatPct(abatePct)} abated for ${formatYears(abateYears)}`,
      nominalCost: null, // depends on calc-engine real-property tax stream
    });
  }

  return { levers, isEmpty: levers.length === 0 };
}

function formatPct(value: number): string {
  // Single decimal unless it's a whole number, e.g. "5%" or "2.5%".
  const pct = value * 100;
  return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(1)}%`;
}

/** "100/100/80/80/60/60/40/40/20/20%" — slash-joined whole-percent schedule. */
function formatSchedule(schedule: readonly number[]): string {
  return `${schedule.map((frac) => Math.round(frac * 100)).join('/')}%`;
}

function formatYears(n: number): string {
  return n === 1 ? '1 year' : `${n} years`;
}

function formatUsd(n: number): string {
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}
